/* The shared vocabulary tables (stage, status and health labels, document families, pills)
   are module-level constants read all over the console. Rather than change every consumer
   to call `t()`, each table exposes its labels as getters that resolve through i18next when
   they are read, so the label always follows the language the hub is currently in. The
   English default is passed through unchanged, so English output is byte-identical. */

import i18n from "./index";

/** A translated string, resolved now; `fallback` is the English original. */
export function lz(key: string, fallback: string): string {
  return i18n.t(key, fallback);
}

function define(target: object, prop: PropertyKey, key: string, fallback: string): void {
  Object.defineProperty(target, prop, {
    enumerable: true,
    configurable: true,
    get: () => i18n.t(key, fallback),
  });
}

/** `{ a: "A" }` under prefix `p` -> `{ get a() { return t("p.a", "A") } }`. */
export function localizedRecord<K extends string>(prefix: string, base: Record<K, string>): Record<K, string> {
  const out = {} as Record<K, string>;
  for (const key of Object.keys(base) as K[]) define(out, key, `${prefix}.${key}`, base[key]);
  return out;
}

/** A `{ value, label }` option list whose `label` is keyed by `value` under `prefix`. */
export function localizedOptions<T extends { value: string; label: string }>(
  prefix: string,
  items: readonly T[],
): T[] {
  return items.map((item) => {
    const copy = { ...item };
    define(copy, "label", `${prefix}.${item.value}`, item.label);
    return copy;
  });
}

/** A copy of `base` whose listed string `fields` resolve under `prefix.<field>`. Null fields stay null. */
export function localizedFields<T extends object>(
  prefix: string,
  base: T,
  fields: readonly (keyof T & string)[],
): T {
  const copy = { ...base };
  for (const field of fields) {
    const value = base[field];
    if (typeof value === "string") define(copy, field, `${prefix}.${field}`, value);
  }
  return copy;
}

/** A `[tone, label]` pill tuple whose label (index 1) is localized. */
export function localizedPill<Tone extends string>(key: string, tone: Tone, label: string): [Tone, string] {
  const pill = [tone] as unknown as [Tone, string];
  define(pill, 1, key, label);
  return pill;
}

/** An array whose items are computed each time they are read, so a list built from localized
    labels at module scope still follows the current language. */
export function lazyList<T>(count: number, get: (index: number) => T): T[] {
  const out: T[] = [];
  for (let i = 0; i < count; i++) {
    Object.defineProperty(out, i, { enumerable: true, configurable: true, get: () => get(i) });
  }
  return out;
}

/** Text the API supplies (role names, report titles). English shows the server's own words
    verbatim; another language looks the stable key up in the catalog and falls back to the
    server text when there is no entry. */
export function serverText(key: string, text: string): string {
  return i18n.language === "en" ? text : i18n.t(key, text);
}
